import React from 'react';

import { useSelector } from 'react-redux';

import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { token } from '@atlaskit/tokens';

import { Commit } from 'src/components/types';
import { useIntl } from 'src/hooks/intl';
import {
  getCurrentUserKey,
  getCurrentUser,
} from 'src/selectors/user-selectors';
import urls from 'src/urls/commit';
import { ui } from 'src/urls/source/ui';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { DiffSettingsButton } from '../../pull-request/components/diff-settings';
import { useCommitApproveHooks } from '../hooks/commit-approve-hooks';
import { UpdateCommitAction } from '../hooks/commit-page-reducer';
import { useCommitWatchHooks } from '../hooks/commit-watch-hooks';

import messages from './commit-page.i18n';

type HeaderActionsProps = {
  commit: Commit;
  dispatchCommitUpdate: (action: UpdateCommitAction) => void;
};

export const HeaderActions = ({
  commit,
  dispatchCommitUpdate,
}: HeaderActionsProps) => {
  const intl = useIntl();
  const currentUserKey = useSelector(getCurrentUserKey) || '';
  const currentUser = useSelector(getCurrentUser);

  const sourcePath = ui.source(commit.repository.full_name, {
    refOrHash: commit.hash,
  });

  const { approveCommit, unapproveCommit, isApproved, isApproveLoading } =
    useCommitApproveHooks(
      commit,
      currentUserKey,
      currentUser,
      dispatchCommitUpdate
    );

  const { watchCommit, unwatchCommit, isWatching, isWatchLoading } =
    useCommitWatchHooks(commit, currentUserKey);

  const HeaderDropdownMenu = () => (
    <DropdownMenu
      trigger={({ triggerRef, ...props }) => (
        <Button
          {...props}
          iconBefore={<MoreIcon label="more" />}
          ref={triggerRef}
        />
      )}
      spacing="compact"
      placement="bottom-end"
      isLoading={isWatchLoading}
      testId="commit-more-button"
    >
      <DropdownItemGroup>
        <DropdownItem
          onClick={() =>
            publishUiEvent({
              action: 'clicked',
              actionSubject: 'link',
              actionSubjectId: 'viewRawCommitLink',
              source: 'commitPage',
            })
          }
          href={urls.ui.rawCommit(commit.repository.full_name, commit.hash)}
        >
          {intl.formatMessage(messages.viewRawCommit)}
        </DropdownItem>
        {currentUserKey === '' ? null : isWatching ? (
          <DropdownItem onClick={unwatchCommit}>
            {intl.formatMessage(messages.unwatchCommit)}
          </DropdownItem>
        ) : (
          <DropdownItem onClick={watchCommit}>
            {intl.formatMessage(messages.watchCommit)}
          </DropdownItem>
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  );

  return (
    <ButtonGroup>
      <Button
        href={sourcePath}
        onClick={() =>
          publishUiEvent({
            action: 'clicked',
            actionSubject: 'link',
            actionSubjectId: 'viewSourceLink',
            source: 'commitPage',
          })
        }
      >
        {intl.formatMessage(messages.viewSource)}
      </Button>
      {isApproved ? (
        <LoadingButton
          onClick={unapproveCommit}
          iconBefore={
            <CheckCircleIcon
              label={intl.formatMessage(messages.unapprove)}
              primaryColor={token('color.icon.success')}
            />
          }
          isLoading={isApproveLoading}
          isSelected
        >
          {intl.formatMessage(messages.unapprove)}
        </LoadingButton>
      ) : (
        <LoadingButton
          onClick={approveCommit}
          iconBefore={
            <CheckCircleIcon label={intl.formatMessage(messages.approve)} />
          }
          isLoading={isApproveLoading}
        >
          {intl.formatMessage(messages.approve)}
        </LoadingButton>
      )}
      <HeaderDropdownMenu />
      <DiffSettingsButton />
    </ButtonGroup>
  );
};
